/* eslint-disable react-refresh/only-export-components -- intentional: step exports a helper alongside the component */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Image as ImageIcon, Loader2, RefreshCw, Save, Trash2 } from 'lucide-react';

import {
  useGenerateAvatarMutation,
  useGetJobStatusQuery,
} from '@/services/avatarApi';
import { cn } from '@/lib/cn';

import { StepError } from '../components/StepError';
import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  merge: (patch: Partial<WizardState>) => void;
  /** Advance to the motion-brief step. Same as the shell footer Next. */
  onContinue: () => void;
  /** Keep the generated avatar, exit the wizard, navigate to the avatar page. */
  onSaveAndExit: () => void;
  /** Delete the generated avatar and exit the wizard. */
  onDiscard: () => void;
}

/**
 * Fires the avatar generation when the user lands on this step and no
 * avatarId is set yet. Polls /jobs/:id every 2s until complete/failed.
 * The user can retry on failure or go back to edit the prompt.
 */
export function Step3Generate({ state, update, merge, onContinue, onSaveAndExit, onDiscard }: Props) {
  const { t } = useTranslation('avatars');
  const [generateAvatar, generateLifecycle] = useGenerateAvatarMutation();
  const [localError, setLocalError] = useState<string | null>(null);
  // React 18 + StrictMode double-fires effects in dev. Without this guard,
  // Step3Generate posted TWO generateAvatar requests on mount — creating
  // two duplicate avatars per wizard run. The ref survives the strict-mode
  // remount cycle while the closure stays stable.
  const firedOnce = useRef(false);

  const fireGeneration = useCallback(async () => {
    setLocalError(null);
    try {
      const sourceImage =
        state.sourceImageDataUrl && state.sourceImageName && state.sourceImageType
          ? dataURLtoFile(state.sourceImageDataUrl, state.sourceImageName, state.sourceImageType)
          : undefined;
      const res = await generateAvatar({
        prompt: state.prompt,
        title: state.title || undefined,
        templateId: state.templateId,
        model: state.model,
        aspectRatio: state.aspectRatio,
        transparent: state.transparent,
        sourceImage,
      }).unwrap();
      update('jobId', res.jobId);
    } catch (err: unknown) {
      console.error('[Step3Generate] generateAvatar failed', err);
      const e = err as { data?: { error?: string }; message?: string };
      // Il messaggio del server resta com'è; si traduce solo il ripiego scritto qui.
      setLocalError(e?.data?.error ?? e?.message ?? t('generatingOverlay.failed'));
    }
  }, [generateAvatar, state, update, t]);

  // Auto-fire once when the step mounts and we don't already have a job.
  // firedOnce ref guards against React 18 StrictMode's double-effect fire
  // in development — without it the wizard posted two simultaneous
  // generateAvatar requests, producing duplicate avatars.
  useEffect(() => {
    if (firedOnce.current) return;
    if (!state.jobId && !state.avatarId && !generateLifecycle.isLoading && !localError) {
      firedOnce.current = true;
      void fireGeneration();
    }
  }, []);

  // Poll job status.
  const { data: job } = useGetJobStatusQuery(
    state.jobId ?? '',
    {
      skip: !state.jobId || !!state.avatarId,
      pollingInterval: 2000,
    },
  );

  // When the job finishes, capture the avatar reference into state.
  useEffect(() => {
    if (!job) return;
    if (job.status === 'complete' && job.avatar) {
      merge({
        avatarId: job.avatar._id,
        avatarImageUrl: job.avatar.imageUrl,
        avatarHasAlpha: !!job.avatar.hasAlpha,
        jobId: undefined,
      });
    } else if (job.status === 'failed') {
      setLocalError(job.error ?? t('editor.step3.jobFailed'));
    }
  }, [job, merge, t]);

  const phase: 'idle' | 'pending' | 'ready' | 'error' = useMemo(() => {
    if (localError) return 'error';
    if (state.avatarId && state.avatarImageUrl) return 'ready';
    if (state.jobId || generateLifecycle.isLoading) return 'pending';
    return 'idle';
  }, [localError, state.avatarId, state.avatarImageUrl, state.jobId, generateLifecycle.isLoading]);

  const onRetry = useCallback(() => {
    merge({ jobId: undefined, avatarId: undefined, avatarImageUrl: undefined, avatarHasAlpha: undefined });
    setLocalError(null);
    firedOnce.current = true; // we are firing now, block the effect re-fire
    void fireGeneration();
  }, [fireGeneration, merge]);

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">{t('editor.step3.title')}</h1>
        <p className="text-sm text-fg-muted">
          {t('editor.step3.intro')}
        </p>
      </header>

      <div className="flex aspect-square w-full max-w-md items-center justify-center self-center overflow-hidden rounded-2xl border border-black/40 bg-black">
        {phase === 'ready' && state.avatarImageUrl ? (
          <img src={state.avatarImageUrl} alt="" className="h-full w-full object-cover" />
        ) : phase === 'pending' ? (
          <div className="flex flex-col items-center gap-3 text-fg-muted">
            <Loader2 aria-hidden className="h-8 w-8 animate-spin text-brand" />
            <p className="text-sm">
              {job ? `${job.progress.step} · ${job.progress.percentage}%` : t('animationOverlay.starting')}
            </p>
          </div>
        ) : (
          <ImageIcon aria-hidden className="h-12 w-12 text-fg-muted/30" />
        )}
      </div>

      {phase === 'error' && (
        <StepError
          title={t('editor.step3.errorTitle')}
          detail={localError ?? undefined}
          actions={[
            { label: t('common.retry'), onClick: onRetry, variant: 'primary' },
          ]}
        />
      )}

      {phase === 'ready' && (
        <div className="flex flex-col gap-3">
          <p className="text-center text-sm text-fg-muted">
            {t('editor.step3.whatNext')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <ActionBtn
              label={t('editor.step3.continue')}
              detail={t('editor.step3.continueHint')}
              icon={<ArrowRight aria-hidden className="h-4 w-4" />}
              onClick={onContinue}
              variant="brand"
            />
            <ActionBtn
              label={t('common.retry')}
              detail={t('editor.step3.regenerateHint')}
              icon={<RefreshCw aria-hidden className="h-4 w-4" />}
              onClick={onRetry}
              variant="muted"
            />
            <ActionBtn
              label={t('editor.step3.save')}
              detail={t('editor.step3.saveHint')}
              icon={<Save aria-hidden className="h-4 w-4" />}
              onClick={onSaveAndExit}
              variant="muted"
            />
            <ActionBtn
              label={t('editor.step3.discard')}
              detail={t('editor.step3.discardHint')}
              icon={<Trash2 aria-hidden className="h-4 w-4" />}
              onClick={() => {
                if (confirm(t('editor.step3.discardConfirm'))) onDiscard();
              }}
              variant="danger"
            />
          </div>
        </div>
      )}
    </section>
  );
}

function ActionBtn({ label, detail, icon, onClick, variant }: {
  label: string;
  detail: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant: 'brand' | 'muted' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1 rounded-xl px-3 py-2.5 text-left transition-opacity active:opacity-80',
        variant === 'brand' && 'bg-brand text-brand-fg',
        variant === 'muted' && 'bg-bg-secondary text-fg',
        variant === 'danger' && 'bg-red-500/10 text-red-400',
      )}
    >
      <span className="flex items-center gap-1.5 text-sm font-semibold">
        {icon}
        {label}
      </span>
      <span className={cn('text-[11px]', variant === 'brand' ? 'text-brand-fg/80' : 'text-fg-muted')}>
        {detail}
      </span>
    </button>
  );
}

export function step3Valid(state: WizardState): boolean {
  return !!state.avatarId && !!state.avatarImageUrl;
}

function dataURLtoFile(dataUrl: string, name: string, type: string): File {
  const [, base64] = dataUrl.split(',');
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type });
}
